import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import styled from 'styled-components';
import { UspItemsFragment } from '../generated/graphql';
import { UspItemTitle } from './Header';
import LinkField from './LinkField';
import { EntryIds } from './MainNavigation';

const ROW_HEIGHT_REM = 5.8;
const ROTATION_INTERVAL = 4000;
const TRANSITION_DURATION = 600;

interface Props {
    usp?: UspItemsFragment[];
    setSelectedPageIds: Dispatch<SetStateAction<EntryIds>>;
}

const useUspRotation = (count: number) => {
    const [offset, setOffset] = useState(0);
    const [animate, setAnimate] = useState(true);

    useEffect(() => {
        if (count <= 1) {
            return undefined;
        }
        const interval = setInterval(() => setOffset(prev => prev + 1), ROTATION_INTERVAL);
        return () => clearInterval(interval);
    }, [count]);

    useEffect(() => {
        if (offset !== count) {
            return undefined;
        }
        const timeout = setTimeout(() => {
            setAnimate(false);
            setOffset(0);
        }, TRANSITION_DURATION);
        return () => clearTimeout(timeout);
    }, [offset, count]);

    useEffect(() => {
        if (animate) {
            return undefined;
        }
        const frame = requestAnimationFrame(() => setAnimate(true));
        return () => cancelAnimationFrame(frame);
    }, [animate]);

    return { offset, animate };
};

interface UspTickerItemProps {
    item: UspItemsFragment;
    onNavigate: () => void;
}

const UspTickerItem: FC<UspTickerItemProps> = ({ item, onNavigate }) => {
    const link = item.uspLink?.[0];
    const title = (
        <UspItemTitle first={false} highlight={false}>
            {item.title}
        </UspItemTitle>
    );

    if (!link || (!link.element?.uri && !link.url)) {
        return <Item>{title}</Item>;
    }

    return (
        <Item>
            <LinkField link={link} onClick={onNavigate}>
                {title}
            </LinkField>
        </Item>
    );
};

const UspSlider: FC<Props> = ({ usp = [], setSelectedPageIds }) => {
    const items = usp.filter(item => Boolean(item.uspLink?.[0]));
    const { offset, animate } = useUspRotation(items.length);

    if (items.length === 0) {
        return null;
    }

    const slides = [...items, items[0]];

    return (
        <Viewport>
            <Track $animate={animate} style={{ transform: `translateY(-${offset * ROW_HEIGHT_REM}rem)` }}>
                {slides.map((item, index) => (
                    <UspTickerItem
                        key={`${item.id ?? 'usp'}-${index}`}
                        item={item}
                        onNavigate={() => setSelectedPageIds([])}
                    />
                ))}
            </Track>
        </Viewport>
    );
};

export default UspSlider;

const Viewport = styled.div`
    position: relative;
    height: ${ROW_HEIGHT_REM}rem;
    padding-top: 1.4rem;
    width: 100%;
    overflow: hidden;
    text-align: center;
    z-index: 1;
`;

const Track = styled.div<{ $animate: boolean }>`
    display: flex;
    flex-direction: column;
    transition: ${({ $animate }) => ($animate ? 'transform 0.6s linear' : 'none')};
`;

const Item = styled.div`
    height: ${ROW_HEIGHT_REM}rem;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding-top: 0.3rem;
`;
